// lib/services/compute/pipelines/animation_compute_pipeline.ts

import ComputePipeline from "../computepipeline";
import ShaderService from "../../shader/shader_service";
import AnimationLibrarySlot from "../animation_library_slot";
import AnimationSlot from "../animation_slot";

const WORKGROUP_SIZE = 64;

export default class AnimationComputePipeline extends ComputePipeline {
  label: string;
  shader_name: string;

  library_bind_group_layout: GPUBindGroupLayout | null = null;
  frame_bind_group_layout: GPUBindGroupLayout | null = null;

  library_bind_group: GPUBindGroup | null = null;
  frame_bind_group: GPUBindGroup | null = null;

  joint_count: number = 0;

  private device: GPUDevice | null = null;

  constructor(key: string, shader_name: string = "animation_controller") {
    super(key + "_animation_compute",64);
    this.label = key + "_animation_compute";
    this.shader_name = shader_name;
  }

  async build(
    device: GPUDevice,
    shader_service: ShaderService
  ): Promise<void> {
    this.device = device;

    const shader = shader_service.get(this.shader_name);

    this.library_bind_group_layout = device.createBindGroupLayout({
      label: this.label + "_library_layout",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // skeleton
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // inverse_binds
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // joint_map
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // clips
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // channels
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // times
        { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // values
        { binding: 7, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },           // character_constants
      ],
    });

    this.frame_bind_group_layout = device.createBindGroupLayout({
      label: this.label + "_frame_layout",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },     // animation_state
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },     // joint_palette
      ],
    });

    const pipeline_layout = device.createPipelineLayout({
      label: this.label + "_pipeline_layout",
      bindGroupLayouts: [this.library_bind_group_layout, this.frame_bind_group_layout],
    });

    this.pipeline = device.createComputePipeline({
      label: this.label,
      layout: pipeline_layout,
      compute: {
        module: shader,
        entryPoint: "cs_main",
      },
    });
  }

  prepare(library_slot: AnimationLibrarySlot, frame_slot: AnimationSlot): void {
    if (!this.device) return;
    if (!this.library_bind_group_layout || !this.frame_bind_group_layout) return;

    this.joint_count = library_slot.joint_count;

    if (
      library_slot.skeleton_buffer &&
      library_slot.inverse_bind_buffer &&
      library_slot.joint_map_buffer &&
      library_slot.clips_buffer &&
      library_slot.channels_buffer &&
      library_slot.times_buffer &&
      library_slot.values_buffer &&
      library_slot.character_constants_buffer
    ) {
      this.library_bind_group = this.device.createBindGroup({
        label: this.label + "_library_bind_group",
        layout: this.library_bind_group_layout,
        entries: [
          { binding: 0, resource: { buffer: library_slot.skeleton_buffer } },
          { binding: 1, resource: { buffer: library_slot.inverse_bind_buffer } },
          { binding: 2, resource: { buffer: library_slot.joint_map_buffer } },
          { binding: 3, resource: { buffer: library_slot.clips_buffer } },
          { binding: 4, resource: { buffer: library_slot.channels_buffer } },
          { binding: 5, resource: { buffer: library_slot.times_buffer } },
          { binding: 6, resource: { buffer: library_slot.values_buffer } },
          { binding: 7, resource: { buffer: library_slot.character_constants_buffer } },
        ],
      });
    } else {
      this.library_bind_group = null;
    }

    if (frame_slot.animation_state_buffer && frame_slot.joint_palette_buffer) {
      this.frame_bind_group = this.device.createBindGroup({
        label: this.label + "_frame_bind_group",
        layout: this.frame_bind_group_layout,
        entries: [
          { binding: 0, resource: { buffer: frame_slot.animation_state_buffer } },
          { binding: 1, resource: { buffer: frame_slot.joint_palette_buffer } },
        ],
      });
    } else {
      this.frame_bind_group = null;
    }
  }

  dispatch(encoder: GPUComputePassEncoder): void {
    if (!this.pipeline || !this.library_bind_group || !this.frame_bind_group) return;
    if (this.joint_count === 0) return;

    encoder.setPipeline(this.pipeline);
    encoder.setBindGroup(0, this.library_bind_group);
    encoder.setBindGroup(1, this.frame_bind_group);

    const workgroup_count = Math.ceil(this.joint_count / WORKGROUP_SIZE);
    encoder.dispatchWorkgroups(workgroup_count, 1, 1);
  }
}